import axios from "axios";
import { baseUrl, imageServiceUrl } from "./baseUrl";
import { toast } from "react-toastify";
import { uploadImage, updateImage } from './imageApiV2';

const token = (localStorage.getItem('cc-t') || sessionStorage.getItem('cc-t'));

// Face Comparison API
export const compareFaces = async (sourceImageUrl, targetImageUrl) => {
    try {
        console.log('🔍 Face comparison called with:', { sourceImageUrl, targetImageUrl });
        
        // Validate input parameters
        if (!sourceImageUrl || !targetImageUrl) {
            throw new Error('Both source and target image URLs are required');
        }
        
        // Check if image service is available first
        // try {
        //     const healthCheck = await axios.get(`${imageServiceUrl}/health`, { timeout: 3000 });
        //     console.log('✅ Image service is available');
        // } catch (healthError) {
        //     console.warn('⚠️ Image service not available, using basic response');
        //     return {
        //         success: false,
        //         match: false,
        //         confidence: 0,
        //         message: "Face comparison service unavailable",
        //         error: "Image service not running on port 4999"
        //     };
        // }
        
        console.log('🌐 Calling real AWS Rekognition API...');
        
        console.log('🔍 Skipping CORS image validation - will let backend handle image fetching...');

        const response = await axios.post(`${imageServiceUrl}/face/compare`, {
            sourceImageUrl,  // User profile photo
            targetImageUrl   // Challenge submission photo
        });
        
        console.log('✅ Real face comparison result:', response.data);
        return response.data;
    } catch (error) {
        console.error('Face comparison API error:', error);
        
        // Handle specific error cases gracefully - don't throw errors, return warning status
        console.warn('⚠️ Face comparison failed, returning warning status');
        
        let message = 'Face comparison unavailable';
        if (error.message.includes('required')) {
            message = 'Missing image URLs for face comparison';
        } else if (error.response?.status === 404) {
            message = 'Face comparison service not available';
        } else if (error.response?.status >= 500) {
            message = 'Face comparison service temporarily unavailable';
        } else if (error.response?.data?.error?.message?.includes('404')) {
            message = 'One or both images not found';
        } else if (error.response?.data?.error?.message?.includes('Failed to fetch image')) {
            message = 'Could not access images for comparison';
        } else {
            message = error.response?.data?.error?.message || error.message || 'Face comparison failed';
        }
        
        return {
            success: false,
            match: false,
            confidence: 0,
            message: message,
            status: 'warning',
            sourceImageUrl,
            targetImageUrl
        };
    }
};

// Create New Challenge Category
export const createChallengeCategory = async (data, image, toast, navigate) => {
    try {
        toast.loading('Creating Challenge Category...');

        // Upload logo image to Image API V2 if provided
        if (image && image instanceof File) {
            console.log('📤 Uploading challenge category logo to Image API V2...');
            const imageResult = await uploadImage(image, 'challenges', false);

            // Only send the logo URL to backend
            data.logo = imageResult.url;

            console.log('✅ Challenge category logo uploaded:', imageResult);
        } else if (image && typeof image === 'string') {
            // If image is already a URL string, use it
            data.logo = image;
        }

        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/categories/create`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
            setTimeout(() => {
                navigate('/challenges/challenge-categories/view-categories');
            }, 2000);
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.dismiss();
            toast.error('An unknown error occurred.');
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// List Challenge Category
export const challengeCategoryList = async (setData, toast) => {
    try {
        // toast.loading('Fetching Challenge Category List...');
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/categories/list`,
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log(res.data);
        if (res.data.success) {
            // toast.dismiss();
            setData(res.data.categories);
            // toast.success('List Challenge Category Refreshed!');
        }
        if (res.data?.error) {
            toast.dismiss();
            // toast.error(res.data?.error?.message);
            toast.error("Could not find categories!");
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

export const getChallengeCategoryList = async () => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/categories/list`,
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            return res.data.categories;
        }
        if (res.data?.error) {
            return [];
        }
    } catch (err) {
        console.log(err);
    }
}

// Get Challenge Category To Edit
export const challengeCategoryEditView = async (id, data, setData, setActive, setImage, toast) => {
    try {
        // toast.loading('Fetching Challenge Category Details...');
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/categories/details/${id}`,
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            // toast.dismiss();
            const updatedFields = data.map(field => ({
                ...field, value: res.data.category[field.name] || ''
            }));
            setData(updatedFields);
            setActive(res.data.category.active);
            setImage(res.data.category.logo);
            // toast.success('List Challenge Category Refreshed!');
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Get Challenge Category To Edit
export const updateChallengeCategory = async (id, data, image, toast) => {
    try {
        toast.loading('Updating Challenge Category Details...');

        // Upload logo image to Image API V2 if new file provided
        if (image && image instanceof File) {
            console.log('📤 Uploading new challenge category logo to Image API V2...');
            const imageResult = await uploadImage(image, 'challenges', false);

            // Only send the logo URL to backend
            data.logo = imageResult.url;

            console.log('✅ Challenge category logo uploaded:', imageResult);
        } else if (image && typeof image === 'string') {
            // If image is a string (existing URL), preserve it
            data.logo = image;
        }

        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/categories/update/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.dismiss();
            toast.error('An unknown error occurred.');
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Delete Challenge Category
export const deleteChallengeCategory = async (id, setData, toast) => {
    try {
        toast.loading('Deleting Challenge Category...');
        const config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/categories/delete/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            setData(res.data.categories);
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// List Challenges
export const challengesList = async (setData, toast, setIsLoading) => {
    try {
        // toast.loading('Fetching Challenge Category List...');
        setIsLoading(true);
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/list`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log(res.data.challenges);
        if (res.data.success) {
            setIsLoading(false);
            toast.dismiss();
            setData(res.data.challenges);
            // toast.success('List Challenge Category Refreshed!');
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

export const getChallengeList = async () => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/list`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log(res.data.challenges);
        if (res.data.success) {
            return res.data.challenges
        }
        // if (res.data?.error) {
        //     toast.dismiss();
        //     toast.error(res.data?.error?.message);
        // }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Create New Challenge
export const createChallenge = async (data, image, toast, toastMessage, navigate, isDraft, setChallengeId) => {
    try {
        toast.loading('Saving Challenge...');

        // Upload cover image to Image API V2 if provided
        if (image && image instanceof File) {
            console.log('📤 Uploading challenge cover image to Image API V2...');
            const imageResult = await uploadImage(image, 'challenges', false);

            data.coverImageId = imageResult.imageId;
            data.coverImageVersion = imageResult.version;
            data.coverImage = imageResult.url;

            console.log('✅ Challenge cover image uploaded:', imageResult);
        }

        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/create`,
            headers: {
                'Content-Type': 'application/json',
                // 'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        // console.log(res.data);
        if (res.data.success) {
            // if(data.isImagePresent){
            //     const imgConfig = {
            //         method: 'put',
            //         maxBodyLength: Infinity,
            //         url: res.data.uploadUrl,
            //         headers: {
            //             'Content-Type': 'multipart/form-data',
            //         },
            //         data: image
            //     };
            //     const imgUpload = await axios.request(imgConfig);
            //     imgUpload && toast.dismiss();
            //     imgUpload && toast.success(res.data.message);
            //     setTimeout(() => {
            //         navigate('/challenges/challenges-main/view-challenges');
            //     }, 2000);
            // }
            toast.dismiss();
            toast.success(toastMessage !== "" ? toastMessage : res.data.message);
            setChallengeId(res.data.challengeId);
            if(!isDraft){
                setTimeout(() => {
                    navigate('/challenges/challenges-main/view-challenges');
                }, 2000);
            }
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.dismiss();
            toast.error('An unknown error occurred.');
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Get Challenge To Edit
export const challengeEditView = async ({
    id,
    setCoverImage,
    setActive,
    setName,
    setCategory,
    setComplexity,
    setSuggestedBy,
    setDisabledFriendly,
    setRecurrent,
    setRecurrentDuration,
    setTypeOfFrequency,
    setFrequency,
    setStartDate,
    setEndDate,
    setDescription,
    setInspireMe,
    setRewardDetails,
    setBonusPoints,
    setSponsoredPoints,
    setTotalPoints,
    setTips,
    setPages,
    setSubmissionProvision,
    setSubmissionButtonType,
    setProductVisible,
    setAppDisplay,
    setMoreChallenges,
    setRecommendedChallenges,
    setChallengeOfTheDay,
    setCarbonEmissionCount,
    setCarbonEmissionInfo,
    setBonusDetails,
    setCarbonSavingPoints,
    setIsSponsored,
    setAttachedProduct,
    toast
}) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/details`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: { challengeId: id }
        };
        const res = await axios.request(config);
        // console.log(res.data.challenge);
        if (res.data.success) {
            const formatDate = (dateString) => {
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            setCoverImage(res.data.challenge.coverImage);
            setActive(res.data.challenge.active);
            setName(res.data.challenge.name);
            setCategory(res.data.challenge.category);
            setComplexity(res.data.challenge.complexity);
            setSuggestedBy(res.data.challenge.suggestedBy.userId);
            setDisabledFriendly(res.data.challenge.inclusive ? "true" : "false");
            setRecurrent(res.data.challenge.recurrent);
            setRecurrentDuration(res.data.challenge.recurrentDuration);
            setTypeOfFrequency(res.data.challenge.typeOfFrequency);
            setFrequency(res.data.challenge.frequency);
            setStartDate(formatDate(res.data.challenge.challengeViewStartDate));
            setEndDate(formatDate(res.data.challenge.challengeViewEndDate));
            setDescription(res.data.challenge.description);
            setInspireMe(res.data.challenge.inspireMe);
            setBonusDetails(res.data.challenge.bonusDetails);
            setRewardDetails(res.data.challenge.rewardDetails);
            setBonusPoints(res.data.challenge.bonusPoints);
            setSponsoredPoints(res.data.challenge.sponsoredPoints);
            setTotalPoints(res.data.challenge.points);
            setTips(res.data.challenge.tips);
            setPages(res.data.challenge.pagesAndQuestions);
            setSubmissionProvision(res.data.challenge.submissionProvision);
            setSubmissionButtonType(res.data.challenge.displaySubmissionButton);
            setProductVisible(res.data.challenge.sponsor.isSponsored);
            setAppDisplay(res.data.challenge.doYouKnowSection);
            setMoreChallenges(res.data.challenge.moreChallenges);
            setRecommendedChallenges(res.data.challenge.recommendedChallenge);
            setChallengeOfTheDay(res.data.challenge.challengeOfTheDay);
            setCarbonEmissionCount(res.data.challenge.carbonEmissionCount);
            setCarbonEmissionInfo(res.data.challenge.carbonEmissionInfo);
            setCarbonSavingPoints(res.data.challenge.carbonSavingPoints);
            setIsSponsored(res.data.challenge?.isSponsored || false);
            setAttachedProduct(res.data.challenge?.attachedProduct || null);
            // console.log({
            //     doYouKnowSection: res.data.challenge.doYouKnowSection,
            //     moreChallenges: res.data.challenge.moreChallenges,
            //     recommendedChallenge: res.data.challenge.recommendedChallenge,
            //     challengeOfTheDay: res.data.challenge.challengeOfTheDay,
            // });
        }
        else {
            console.log(res.data?.error);
        }
    } catch (err) {
        console.log(err);
    }
}

// Edit Challenge
export const editChallenge = async (id, data, image, toast, toastMessage, navigate, isDraft, setChallengeId) => {
    try {
        toast.loading('Saving Challenge...');

        // Upload cover image to Image API V2 if provided
        if (image && image instanceof File) {
            console.log('📤 Uploading challenge cover image to Image API V2...');

            // Use updateImage if coverImageId exists, otherwise upload new
            let imageResult;
            if (data.coverImageId) {
                imageResult = await updateImage(data.coverImageId, image);
                console.log('✅ Challenge cover image updated to version:', imageResult.version);
            } else {
                imageResult = await uploadImage(image, 'challenges', false);
                console.log('✅ Challenge cover image uploaded:', imageResult);
            }

            data.coverImageId = imageResult.imageId;
            data.coverImageVersion = imageResult.version;
            data.coverImage = imageResult.url;
        }

        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/details/edit/${id}`,
            headers: {
                'Content-Type': 'application/json',
                // 'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            toast.success(toastMessage !== "" ? toastMessage : res.data.message);
            setChallengeId(res.data.challengeId);
            if(!isDraft){
                setTimeout(() => {
                    navigate('/challenges/challenges-main/view-challenges');
                }, 2000);
            }
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.dismiss();
            toast.error('An unknown error occurred.');
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Delete Challenge
export const deleteChallenge = async (id, setData, toast) => {
    try {
        toast.loading('Deleting Challenge...');
        const config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/delete/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            setData(res.data.challenges);
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Get image upload URL
export const getImageUploadUrl = async (data) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/get-presigned-url`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data
        };
        const res = await axios.request(config);
        return res.data;
    } catch (err) {
        console.log(err);
    }
}

// Upload Image to Pre-signed URL
export const uploadImageToPresignedUrl = async (url, image) => {
    try {
        const config = {
            method: 'put',
            maxBodyLength: Infinity,
            url: url,
            headers: {
                'Content-Type': image.type,
            },
            data: image
        };
        const res = await axios.request(config);
        return res;
    } catch (error) {
        console.log(error);
    }
}

// Get Suggested User List
export const getSuggestedUsersList = async (setData, toast) => {
    try {
        // toast.loading('Fetching Challenge Category List...');
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/suggested-users-list`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log(res.data);
        if (res.data.success) {
            // toast.dismiss();
            setData(res.data.users);
            // toast.success('List Challenge Category Refreshed!');
        }
        if (res.data?.error) {
            toast.dismiss();
            // toast.error(res.data?.error?.message);
            toast.error("Could not find users!");
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Challenge Submission List
export const challengeSubmissionList = async (setData, toast, setIsLoading, filters = {}, page = 1, limit = 20) => {
    try {
        // toast.loading('Fetching Challenge Submission List...');
        setIsLoading(true);
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/submission-list`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: {
                page,
                limit,
                ...filters
            }
        };
        const res = await axios.request(config);
        // console.log(res.data.submissions);
        if (res.data.success) {
            setIsLoading(false);
            toast.dismiss();
            setData(res.data.submissions);
            // Return pagination data
            return res.data.pagination;
            // toast.success('List Challenge Submission Refreshed!');
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Challenge Submission Details
export const challengeSubmissionDetails = async (id, setData, setSubmissionData, setChallengeData, setDisabledControl, setSelectedTab, toast, setLoading, setError) => {
    try {
        console.log('🌐 Loading submission details for ID:', id);
        
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/submission-details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        
        const res = await axios.request(config);
        
        if (res.data.success) {
            console.log('✅ Submission data loaded successfully');
            // toast.dismiss();
            setData(res.data.submissionDetails);
            console.log('Submission Details:', res.data.submissionDetails);
            setSubmissionData(res.data.submissionDetails.submission);
            setChallengeData(res.data.submissionDetails.challenge);
            setDisabledControl(res.data.submissionDetails.submission.reviewStatus === "submitted" ? true : false);
            if (res.data.submissionDetails.submission.pagesAndQuestions && res.data.submissionDetails.submission.pagesAndQuestions.length > 0) {
                setSelectedTab(res.data.submissionDetails.submission.pagesAndQuestions[0].pageId);
            }
            setTimeout(() => {
                setLoading(false);
            }, 1000);
            // toast.success('List Challenge Submission Refreshed!');
        }
        if (res.data?.error) {
            console.error('❌ API returned error:', res.data.error);
            toast.dismiss();
            toast.error(res.data?.error?.message);
            if(res.data?.error?.message === 'Challenge Not Found!'){
                setError('Challenge deleted!');
            }
        }
    } catch (err) {
        console.error('❌ API call failed:', err);
        console.error('❌ Error response:', err.response?.data);
        console.error('❌ Error status:', err.response?.status);
        toast.dismiss();
        if (err.response?.status === 401) {
            toast.error('Authentication failed. Please log in again.');
            setError('Authentication failed. Please log in again.');
        } else if (err.response?.status === 403) {
            toast.error('Access forbidden. Check your permissions.');
            setError('Access forbidden. Check your permissions.');
        } else if (err.response?.status === 404) {
            toast.error('Submission not found.');
            setError('Submission not found.');
        } else if (err.response?.data?.error?.message) {
            toast.error(err.response.data.error.message);
            setError(err.response.data.error.message);
        } else {
            toast.error('Something Went Wrong!');
            setError('Network error or server unavailable.');
        }
        setLoading(false);
    }
}

// Get Challenge Submission Details
export const getChallengeSubmissionDetails = async (id, setUserData, setSubmissionData, setChallengeData, toast, setLoading, setError) => {
    try {
        // toast.loading('Fetching Challenge Submission List...');
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/submission-details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            setUserData(res.data.submissionDetails);
            console.log('Submission Details:', res.data.submissionDetails);
            setSubmissionData(res.data.submissionDetails.submission);
            setChallengeData(res.data.submissionDetails.challenge);
            setLoading(false);
            // toast.success('List Challenge Submission Refreshed!');
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
            if(res.data?.error?.message === 'Challenge Not Found!'){
                setError('Challenge deleted!');
            }
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Challenge Submission Approve
export const challengeSubmissionApprove = async (data, setSubmissionData, toast, setLoading) => {
    try {
        // toast.loading('Fetching Challenge Submission List...');
        setLoading(true);
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/approve-submission-question`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            // toast.dismiss();
            setLoading(false);
            toast.success('Submission Approved!');
            // setTimeout(() => {
            setSubmissionData(res.data.updateUserSubmission);
            // }, 2000);
        }
        if (res.data?.error) {
            // toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        // toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Challenge Submission Reject
export const challengeSubmissionReject = async (data, setSubmissionData, toast) => {
    try {
        // toast.loading('Fetching Challenge Submission List...');
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/reject-submission-question`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            // toast.dismiss();
            // toast.success('Submission Reject!');
            setSubmissionData(res.data.updateUserSubmission);
            // console.log(res.data.updateUserSubmission);
        }
        if (res.data?.error) {
            // toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        // toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Challenge Submission Verification Submit
export const submitChallengeVerification = async (data, isRejected, setSubmissionData, toast, navigate) => {
    try {
        // toast.loading('Fetching Challenge Submission List...');
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/submit-submission-verification`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            if(isRejected) toast.success("Submission Rejected!");
            if(!isRejected) toast.success('Submission Accepted!');
            // Update submission data if provided by backend
            if (res.data.updateSubmissionData) {
                setSubmissionData(res.data.updateSubmissionData);
            }
            if(isRejected){
                setTimeout(() => {
                    navigate('/submissions/challenge-submission');
                }, 2000);
            }
        }
        if (res.data?.error) {
            // toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        // toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Challenge Submission Verification Update Status
export const submitChallengeVerificationUpdateStatus = async (data, toast, navigate) => {
    try {
        // toast.loading('Fetching Challenge Submission List...');
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/submit-submission-verification-update-status`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            // toast.dismiss();
            toast.success('Submission Updated!');
            setTimeout(() => {
                navigate('/submissions/challenge-submission');
            }, 2000);
        }
        if (res.data?.error) {
            // toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        // toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Get Users for filters
export const getUsersForFilters = async (setData) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/get-users-for-filters`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log(res.data);
        if (res.data.success) {
            setData(res.data.users);
        }
    } catch (err) {
        console.log(err);
    }
}

// Get Challenges for filters
export const getChallengesForFilters = async (setData) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/get-challenges-for-filters`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log(res.data);
        if (res.data.success) {
            setData(res.data.challenges);
        }
    } catch (err) {
        console.log(err);
    }
}

// Get Categories for filters
export const getCategoriesForFilters = async (setData) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/get-categories-for-filters`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log(res.data);
        if (res.data.success) {
            setData(res.data.categories);
        }
    } catch (err) {
        console.log(err);
    }
}

// Get Proposed Challenges
export const getProposedChallenges = async () => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/user-suggested-challenges`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        return res.data?.challenges;
    } catch (error) {
        toast.error('Something Went Wrong!');
    }
}

// Accept Proposed Challenge
export const acceptProposedChallenge = async (data) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/user-suggested-challenge/update-status`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        return res.data;
    } catch (error) {
        toast.error('Something Went Wrong!');
    }
}

// Reject Proposed Challenge
export const rejectProposedChallenge = async (data) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/user-suggested-challenge/update-status`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        return res.data;
    } catch (error) {
        toast.error('Something Went Wrong!');
    }
}

// Get Proposed Challenge Details
export const getProposedChallengeDetails = async (id) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/user-suggested-challenge-details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        return res.data.challenge;
    } catch (error) {
        toast.error('Something Went Wrong!');
    }
}

// Edit Proposed Challenge
export const editProposedChallenge = async (data) => {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/challenges/user-suggested-challenge/update`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        return res.data;
    } catch (error) {
        console.log(error);
    }
}

// get eco quiz list

export const getEcoQuizList = async () => {
    try {
        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/quiz/list-quiz`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log(res.data.quizzes);
        return res.data.quizzes;
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// AI review auto-decision settings (served by ai-review-service; admins only)
const aiAuth = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('cc-t') || sessionStorage.getItem('cc-t')}` } });
export const getAiReviewSettings = async () => (await axios.get(`${baseUrl}/ai-review/settings`, aiAuth())).data.settings;
export const saveAiReviewSettings = async (settings) => (await axios.put(`${baseUrl}/ai-review/settings`, settings, aiAuth())).data.settings;
export const previewAiApplyPast = async (filters) => (await axios.post(`${baseUrl}/ai-review/apply-past/preview`, filters, aiAuth())).data;
export const runAiApplyPast = async (filters) => (await axios.post(`${baseUrl}/ai-review/apply-past/run`, filters, aiAuth())).data.run;
export const getAiApplyPastStatus = async () => (await axios.get(`${baseUrl}/ai-review/apply-past/status`, aiAuth())).data.run;
// AI review rules (editable by the admin): general + per challenge, test before saving, re-check pending.
export const getAiRules = async (challengeId) => (await axios.get(`${baseUrl}/ai-review/rules${challengeId ? `?challengeId=${challengeId}` : ''}`, aiAuth())).data;
export const saveAiRules = async (body) => (await axios.put(`${baseUrl}/ai-review/rules`, body, aiAuth())).data.rules;
export const startAiRulesTest = async (body) => (await axios.post(`${baseUrl}/ai-review/rules/test`, body, aiAuth())).data.test;
export const getAiRulesTest = async (id) => (await axios.get(`${baseUrl}/ai-review/rules/test/${id}`, aiAuth())).data.test;
export const previewAiRecheck = async (challengeId) => (await axios.post(`${baseUrl}/ai-review/rules/recheck/preview`, { challengeId }, aiAuth())).data;
export const runAiRecheck = async (challengeId) => (await axios.post(`${baseUrl}/ai-review/rules/recheck`, { challengeId }, aiAuth())).data;
export const checkAiRules = async (body) => (await axios.post(`${baseUrl}/ai-review/rules/check`, body, aiAuth())).data.check;
